import * as Yup from "yup";
import { IEC_CODES, NORWAY_CODES, REGEX, SWEDEN_CODES } from "../components/Common/constants";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getCountryLabel, getNormalizedCountry, isTodayOrFutureDDMMYYYY } from "../components/Common/utils";

const BIRTH_AND_PASSWORD_FIELDS = ["cityOfBirth", "countryOfBirth", "password"];
const CURRENT_PERSONAL_DETAIL_FIELDS = ["currentFirstName", "currentFullLastName", "currentCompanyEmail"];
const DEFAULT_COUNTRY_FIELDS = new Set(["gender", ...BIRTH_AND_PASSWORD_FIELDS]);
const EMPTY_COUNTRY_FIELDS = new Set();

const COUNTRY_JOURNEY_FIELDS = {
  ADD: {
    DEFAULT: DEFAULT_COUNTRY_FIELDS,
    IEC: EMPTY_COUNTRY_FIELDS,
    SWEDEN: new Set(BIRTH_AND_PASSWORD_FIELDS),
    NORWAY: DEFAULT_COUNTRY_FIELDS,
  },
  MODIFY: {
    DEFAULT: DEFAULT_COUNTRY_FIELDS,
    IEC: EMPTY_COUNTRY_FIELDS,
    SWEDEN: new Set([...BIRTH_AND_PASSWORD_FIELDS, ...CURRENT_PERSONAL_DETAIL_FIELDS]),
    NORWAY: new Set(["gender", ...BIRTH_AND_PASSWORD_FIELDS, ...CURRENT_PERSONAL_DETAIL_FIELDS]),
  },
};

export function useEUFormConfig({ user, modifyUser }) {
  const [initialValues, setInitialValues] = useState({});

  const { t } = useTranslation("common");


  const defaultValues = {
    // Personal Info
    firstName: "",
    fullMiddleName: "",
    lastName: "",
    paAddress: "",
    paPostcode: "",
    countryUK: "",
    paCountry: "",
    paExistingInfo: "",
    gender: "",
    city: "",
    // PA Screening
    day: "",
    month: "",
    year: "",
    nationality: "",
    cityOfBirth: "",
    countryOfBirth: "",
    personalIdNumber: "",
    password: "",
    supportingFiles: [],
    paScreeningFiles: [],
    // PA Security/Login
    motherMaidenName: "",
    verificationPin: "",
    memorableDate: "",
    clueMemorableDate: "",
    // Contact Info
    phoneNumber: "",
    emailId: "",
    // Company Info
    bussRegName: "",
    bussRegAddress: "",
    bussRegPostcode: "",
    bussRegCountry: "",
    companyType: "",
    organisationNumber: "",
    // Correspondence Info
    empBussName: "",
    corrAddress: "",
    corrPostcode: "",
    corrCountry: "",
    modifypaPermissionOnline: false,
    // Politically Exposed Person Info
    notPoliticallyExposedPerson: false,
    familyOfPoliticallyExposedPerson: false,
    politicallyExposedPerson: false,
    politicallyExposedPersonDropdown: "",
    pepTitle: "",
    pepCountry: "",
    pepOrganization: "",
    //Photo ID Info
    photoIdNumber: "",
    typeOfDocument: "",
    countryOfIssuance: "",
    photoExpiryDate: "",
    currentFirstName: "",
    currentFullMiddleName: "",
    currentFullLastName: "",
    currentCompanyEmail: "",
  };

  useEffect(() => {
    if (user?.country) {
      setInitialValues({
        ...defaultValues,
        ...user,
      });
    }
  }, [user?.country]);

  const country = user?.country || initialValues.paCountry || "";
  const countryNorm = getNormalizedCountry(country);
  const isSweden = SWEDEN_CODES.includes(countryNorm);
  const isNorway = NORWAY_CODES.includes(countryNorm);
  const isIec = IEC_CODES.map((code) => getNormalizedCountry(code)).includes(countryNorm);
  const isIecModifyUser = modifyUser && isIec;
  const isEuroModifyUser = modifyUser && (isSweden || isNorway || isIec);
  const countryKey = isIec ? "IEC" : isSweden ? "SWEDEN" : isNorway ? "NORWAY" : "DEFAULT";
  const journeyKey = modifyUser ? "MODIFY" : "ADD";
  const countryJourneyFields = COUNTRY_JOURNEY_FIELDS[journeyKey][countryKey];
  const shouldValidateCountryField = (field) => countryJourneyFields.has(field);
  const fileShape = Yup.object().shape({
    documentName: Yup.string().required(),
    base64Content: Yup.string().required(),
    mimeType: Yup.string().required(),
  });

  const conditionalCheck = (requiredKey = null, { regex = null, regexErrKey = null } = {}, isOptional = false) => {
    if (isEuroModifyUser && !isOptional) {
      return Yup.string().test(
        `${requiredKey || "optional"}-format-or-empty`,
        t(regexErrKey || requiredKey),
        (val) => !val || (regex ? regex.test(val) : true)
      );
    }
    let s = Yup.string();
    if (requiredKey) s = s.required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const mandatoryCheck = (requiredKey = null, { regex = null, regexErrKey = null } = {}) => {
    let s = Yup.string();
    if (requiredKey) s = s.required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const screeningFieldCheck = (requiredKey, { regex = null, regexErrKey = null } = {}) => {
    if (isEuroModifyUser) {
      return Yup.string().test(
        `${requiredKey}-format-or-empty`,
        t(regexErrKey || requiredKey),
        (val) => !val || (regex ? regex.test(val) : true)
      );
    }
    if ((isSweden || isNorway) && !modifyUser) {
      return Yup.string().when(['personalIdNumber'], ([personalIdNumber], schema) => {
        if (personalIdNumber) {
          return regex
            ? schema.test(`${requiredKey}-format-optional`, t(regexErrKey), val => !val || regex.test(val))
            : schema;
        }
        let s = schema.required(t(requiredKey));
        if (regex) s = s.matches(regex, t(regexErrKey));
        return s;
      });
    }
    let s = Yup.string().required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const countryLabel = getCountryLabel(user?.country, t);

  const contactInfoFields = {
    INTERNATIONALEUROCARD: {
      bussRegName: Yup.string().required(t("newuser.businessRegNameRequired")),
      bussRegAddress: Yup.string().required(t("newuser.businessRegAddressRequired")),
      bussRegPostcode: conditionalCheck("newuser.businessRegPostcodeRequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.businessRegPostcodeError" }, true),
      bussRegCountry: Yup.string().required(t("newuser.businessRegCountryRequired")),
    },
    DEFAULT: {
      bussRegName: Yup.string().required(t("newuser.bussregnamerequired")),
      bussRegAddress: Yup.string().required(t("newuser.bussregaddressrequired")),
      bussRegPostcode: conditionalCheck("newuser.bussregaddressrequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.bussRegPostcodeerror" }),
      bussRegCountry: Yup.string().required(t("newuser.bussregcountryrequired")),
    }
  };

  const correspondenceInfoFields = {
    INTERNATIONALEUROCARD: {
      empBussName: Yup.string(),
      corrPostcode: Yup.string().test(
        "corrPostcode-format",
        t("newuser.corrpostcodeerror"),
        (val) => !val || REGEX.NUMBER.test(val)
      ),
      corrAddress: Yup.string(),
      corrCountry: Yup.string(),
    },
    DEFAULT: {
      empBussName: Yup.string(),
      corrAddress: Yup.string(),
      corrPostcode: Yup.string().test(
        "corrPostcode-format",
        t("newuser.corrpostcodeerror"),
        (val) => !val || REGEX.NUMBER.test(val)
      ),
      corrCountry: Yup.string(),
    }
  };

  const validationSchema = Yup.object({
    firstName: conditionalCheck("newuser.fnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.fnerror" }),
    fullMiddleName: Yup.string(),
    lastName: conditionalCheck("newuser.lnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.lnerror" }),
    paAddress: conditionalCheck("newuser.paaddressrequired"),
    paPostcode: conditionalCheck("newuser.papostcoderequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.papostcodeerror" }),
    countryUK: conditionalCheck("newuser.countryrequired"),
    paCountry: conditionalCheck("newuser.pacountryrequired"),
    paExistingInfo: isIec
      ? conditionalCheck(null, { regex: REGEX.ALPHANUMERIC, regexErrKey: "newUserUK.paExitInfoError" })
      : conditionalCheck(),
    gender: shouldValidateCountryField("gender")
      ? conditionalCheck("newuser.genderrequired")
      : Yup.string(),
    city: (isSweden || isNorway) && !modifyUser
      ? Yup.string()
          .required(t("newuser.cityrequired"))
          .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.cityerror"))
      : (isSweden || isNorway)
        ? Yup.string().matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.cityerror"))
        : Yup.string(),
    day: screeningFieldCheck("newuser.dayrequired", { regex: REGEX.TWO_DIGIT_NUMBER, regexErrKey: "newuser.dayerror" })
      .test("valid-day", t("newuser.dayerror"), val => !val || (Number(val) >= 1 && Number(val) <= 31)),
    month: screeningFieldCheck("newuser.monthrequired", { regex: REGEX.TWO_DIGIT_NUMBER, regexErrKey: "newuser.montherror" })
      .test("valid-month", t("newuser.monthValid"), val => !val || (Number(val) >= 1 && Number(val) <= 12)),
    year: screeningFieldCheck("newuser.yearRequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.validYear" }),
    nationality: screeningFieldCheck("newuser.nationalityRequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.nationalityError" }),
    cityOfBirth: shouldValidateCountryField("cityOfBirth")
      ? screeningFieldCheck("newuser.ciobrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.cioberror" })
      : Yup.string(),
    countryOfBirth: shouldValidateCountryField("countryOfBirth")
      ? screeningFieldCheck("newuser.cnobrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.cnoberror" })
      : Yup.string(),
    personalIdNumber: Yup.string().test(
      "personalId-conditional",
      t("newuser.personalidnumbererror"),
      function (val) {
        if (isEuroModifyUser) {
          return !val || REGEX.PERSONAL_ID_NUMBER.test(val);
        }
        const { day, month, year, nationality, cityOfBirth, countryOfBirth } = this.parent;
        const hasScreeningFields = day || month || year || nationality || cityOfBirth || countryOfBirth;
        if (!hasScreeningFields && !val) {
          return this.createError({ message: t("newuser.personalidrequiredconditional") });
        }
        if (val && !REGEX.PERSONAL_ID_NUMBER.test(val)) {
          return this.createError({ message: t("newuser.personalidnumbererror") });
        }
        return true;
      }
    ),
    password: shouldValidateCountryField("password")
      ? conditionalCheck("newuser.passwordrequired").min(8, t("newuser.passwordminlength"))
      : Yup.string(),
    paScreeningFiles: modifyUser
      ? Yup.array().of(fileShape)
      : (isIec || isSweden || isNorway)
        ? Yup.array().min(1, t("access.uploaderror")).of(fileShape)
        : Yup.array().of(fileShape),
    // PA Security/Login
    verificationPin: conditionalCheck("newuser.verificationpinukrequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.verificationpinukerror" }),
    memorableDate: conditionalCheck("newuser.memorableDateUkRequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.memorableDateUkError" }),
    motherMaidenName: isIec
      ? conditionalCheck("newuser.mothersMaidenNameRequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.mothersMaidenNameError" })
      : Yup.string(),
    clueMemorableDate: conditionalCheck("newuser.cluememorabledateukrequired"),
    // Contact Info
    phoneNumber: isIec
      ? (isIecModifyUser ? Yup.string() : Yup.string().required(t("newuser.phonenumberrequired")))
      : conditionalCheck("newuser.phonenumberrequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.phonenumbererror" }),
    emailId: isIec
      ? (isIecModifyUser ? Yup.string() : Yup.string().required(t("newuser.emailrequire")))
      : conditionalCheck("newuser.emailrequire", { regex: REGEX.EMAIL, regexErrKey: "newuser.emailerror" }),
    // Company Info
    ...contactInfoFields[countryLabel],
    organisationNumber: (isSweden || isNorway)
      ? Yup.string()
          .required(t("newuser.organisationnumberrequired"))
          .test(
            "organisation-number-format",
            t("newuser.organisationnumbererror"),
            function (val) {
              if (!val) return false;
              if (isSweden) {
                return REGEX.NUMBER_HYPHEN.test(val);
              }
              return REGEX.NUMBER.test(val);
            }
          )
      : Yup.string(),
    // Correspondence Info
    ...correspondenceInfoFields[countryLabel],
    notPoliticallyExposedPerson: (isSweden || isNorway) && !modifyUser
      ? Yup.boolean().test(
          "pep-selection-required",
          t("newuser.pepSelectionRequired"),
          function (val) {
            const { familyOfPoliticallyExposedPerson, politicallyExposedPerson } = this.parent;
            return val || familyOfPoliticallyExposedPerson || politicallyExposedPerson;
          }
        )
      : Yup.boolean(),
    familyOfPoliticallyExposedPerson: Yup.boolean(),
    politicallyExposedPerson: Yup.boolean(),
    politicallyExposedPersonDropdown: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepPositionRequired"))
                .notOneOf(["dropdown_menu"], t("newuser.pepPositionRequired"))
            : schema
      ),
    pepTitle: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepTitleRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.titleerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.titleerror"))
      ),
    pepCountry: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepCountryRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.countryerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.countryerror"))
      ),
    pepOrganization: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepOrganizationRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.organizationerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.organizationerror"))
      ),
    photoIdNumber: isIec
      ? conditionalCheck("newuser.photoIdNumberRequired", { regex: REGEX.ALPHANUMERIC_HYPHEN, regexErrKey: "newuser.photoIdNumberError" })
      : Yup.string(),
    typeOfDocument: isIec && !modifyUser
      ? conditionalCheck("newuser.typeOfDocumentRequired")
      : Yup.string().nullable(),
    countryOfIssuance: isIec
      ? conditionalCheck("newuser.countryOfIssuanceRequired",
            {
              regex: REGEX.LETTER_SPACE_HYPHEN,
              regexErrKey: "newuser.countryerror",
            }).trim()
      : Yup.string().nullable(),
    photoExpiryDate: isIec
      ? conditionalCheck("newuser.photoExpiryDateRequired", { regex: REGEX.DDMMYYYY, regexErrKey: "newuser.photoExpiryDateError" })
          .test(
            "photo-expiry-date-not-expired",
            t("newuser.photoExpiryDateRequired"),
            (val) => !val || isTodayOrFutureDDMMYYYY(val)
          )
      : Yup.string(),
    currentFirstName: shouldValidateCountryField("currentFirstName")
      ? mandatoryCheck("newuser.fnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.fnerror" })
      : Yup.string(),
    currentFullMiddleName: Yup.string(),
    currentFullLastName: shouldValidateCountryField("currentFullLastName")
      ? mandatoryCheck("newuser.lnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.lnerror" })
      : Yup.string(),
    currentCompanyEmail: shouldValidateCountryField("currentCompanyEmail")
      ? mandatoryCheck("newuser.emailrequire", { regex: REGEX.EMAIL, regexErrKey: "newuser.emailerror" })
      : Yup.string(),
  });

  return { initialValues, validationSchema };
}
